import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Mail } from "lucide-react";
import axios from "axios";
import Seo from "@/components/Seo";
import { formatApiError } from "@/lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/account/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err, "Could not send the reset email. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Seo title="Forgot password — EI Battery Solutions" description="Request a password reset link for your EI Battery trade account." />
      <section className="max-w-md mx-auto px-4 py-16 sm:py-24" data-testid="forgot-password-page">
        <h1 className="text-3xl font-bold text-ei-navy">Forgot your password?</h1>
        <p className="mt-2 text-sm text-ei-graphite/75">Enter the email on your trade account and we'll send you a link to set a new password. The link lasts 1 hour and can be used once.</p>
        {sent ? (
          <div className="mt-8 rounded-sm border border-ei-gold/40 bg-ei-gold/10 p-5 text-sm text-ei-graphite" data-testid="forgot-password-sent">
            <p>If that email is on a trade account, a reset link is on its way. Please check your inbox, including junk mail.</p>
            <Link to="/account/login" className="mt-3 inline-block font-semibold text-ei-navy underline hover:text-ei-golddark" data-testid="forgot-password-back">Back to sign in</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="forgot-password-form">
            <label className="block">
              <span className="text-xs font-semibold text-ei-navy">Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="forgot-password-email"
                className="mt-1 w-full border border-ei-mist rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-ei-navy"
              />
            </label>
            {error && <p className="text-sm text-red-700" data-testid="forgot-password-error">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              data-testid="forgot-password-submit"
              className="w-full font-display text-sm font-bold bg-ei-navy text-ei-white px-6 py-3.5 rounded-sm hover:bg-ei-ink transition-colors inline-flex items-center justify-center gap-2 min-h-[48px] disabled:opacity-60"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Send reset link
            </button>
            <Link to="/account/login" className="block text-sm text-ei-graphite/70 hover:text-ei-navy" data-testid="forgot-password-cancel">Back to sign in</Link>
          </form>
        )}
      </section>
    </>
  );
}
